import { useEffect, useState, useSyncExternalStore } from 'react';
import { contractorToken } from './contractorAuth';

/**
 * PROFILE PHOTOS, as the page knows them (migration 080).
 *
 * One small store for the whole app: which people have a photo this person
 * may see, and at which version. Every circle (components/Avatar.js) reads
 * it, so the list is asked for ONCE however many circles a screen draws -
 * a chat list of forty people is one request, not forty 404s. It is asked
 * again every few minutes while the tab is in front, so somebody's new photo
 * reaches colleagues without a reload, and at once for your own.
 *
 * The pictures themselves are fetched with the signed-in person's own
 * credential (the API never serves one to nobody), turned into object URLs
 * and kept for the life of the tab. Their address carries the version, so
 * the browser's own cache keeps each for a year and a new photo is simply a
 * new address.
 *
 * Anything that goes wrong - an older server, a database without 080, a
 * guest with no identity, a test that answers everything with [] - leaves
 * the store empty, and an empty store is every circle showing its initials,
 * which is exactly what Jarvis did before.
 */
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const REFRESH_MS = 5 * 60 * 1000;

const EMPTY = Object.freeze({ loaded: false, setting: 'allowed', photos: {}, me: null });
let state = EMPTY;
let inflight = null;
let loadedAt = 0;
let timer = null;
const listeners = new Set();

const emit = () => listeners.forEach((fn) => { try { fn(); } catch { /* a listener must not break the rest */ } });
const set = (next) => { state = next; emit(); };

/** A contractor's portal token when one is signed in; the staff token otherwise (lib/apiClient). */
export const photoAuth = () => {
  const token = contractorToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const parse = (data) => {
  const photos = data && data.photos && typeof data.photos === 'object' && !Array.isArray(data.photos)
    ? Object.fromEntries(Object.entries(data.photos).filter(([k, v]) => /^[uc]:/.test(k) && typeof v === 'string'))
    : {};
  const me = data && data.me && typeof data.me === 'object' && typeof data.me.key === 'string' ? data.me : null;
  const setting = ['allowed', 'staff', 'off'].includes(data?.setting) ? data.setting : 'allowed';
  return { loaded: true, setting, photos, me, ready: Boolean(me) };
};

/** Ask the server which photos there are. Shared by everybody asking at once. */
export function loadPhotos({ force = false } = {}) {
  if (!force && inflight) return inflight;
  if (!force && state.loaded && Date.now() - loadedAt < REFRESH_MS) return Promise.resolve(state);
  inflight = fetch(`${API_BASE}/photos`, { headers: photoAuth() })
    .then(async (r) => (r.ok ? parse(await r.json().catch(() => null)) : { ...EMPTY, loaded: true, unavailable: r.status }))
    .catch(() => ({ ...EMPTY, loaded: true, unavailable: 0 }))
    .then((next) => {
      inflight = null;
      loadedAt = Date.now();
      set(next);
      return next;
    });
  return inflight;
}

const startRefreshing = () => {
  if (timer || typeof window === 'undefined') return;
  timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') loadPhotos();
  }, REFRESH_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - loadedAt > 60 * 1000) loadPhotos({ force: true });
  });
};

const subscribe = (fn) => {
  listeners.add(fn);
  // A page that asked before anybody had signed in asks again soon after.
  if (!state.loaded) loadPhotos();
  else if (state.unavailable === 401 && Date.now() - loadedAt > 15 * 1000) loadPhotos({ force: true });
  startRefreshing();
  return () => listeners.delete(fn);
};
const snapshot = () => state;

/** The whole store: { loaded, setting, photos, me }. */
export const usePhotos = () => useSyncExternalStore(subscribe, snapshot, snapshot);

/** The version of this person's photo I may see, or null. */
export function usePhotoVersion(key) {
  const s = usePhotos();
  return (key && s.photos[key]) || null;
}

/** Where one picture lives. `size` is 96 (every circle) or 512 (large). */
export const photoUrl = (key, version, size = 96) => {
  const [kind, id] = String(key).split(':');
  return `${API_BASE}/photos/${kind}/${encodeURIComponent(id)}?size=${size === 512 ? 512 : 96}&v=${encodeURIComponent(version || '')}`;
};

/* --------------------------------------------------------- the pictures */

const blobs = new Map();        // url -> Promise<objectURL>
const ready = new Map();        // url -> objectURL, once known
const failed = new Set();       // urls that could not be had, not asked for again this tab

function picture(url) {
  if (ready.has(url)) return Promise.resolve(ready.get(url));
  if (failed.has(url)) return Promise.reject(new Error('unavailable'));
  if (!blobs.has(url)) {
    blobs.set(url, fetch(url, { headers: photoAuth() })
      .then(async (r) => {
        if (!r.ok) throw new Error(String(r.status));
        const type = r.headers.get('content-type') || '';
        if (!/^image\/(webp|jpeg)/.test(type)) throw new Error('not a photo');
        const u = URL.createObjectURL(await r.blob());
        ready.set(url, u);
        return u;
      })
      .catch((e) => { failed.add(url); blobs.delete(url); throw e; }));
  }
  return blobs.get(url);
}

/**
 * The object URL of somebody's photo at `size`, or null while there is none
 * (no photo, still loading, failed). The circle draws its initials for null.
 */
export function usePhotoSrc(key, size = 96) {
  const version = usePhotoVersion(key);
  const url = version ? photoUrl(key, version, size) : null;
  const [src, setSrc] = useState(() => (url && ready.get(url)) || null);
  useEffect(() => {
    if (!url) { setSrc(null); return undefined; }
    if (ready.has(url)) { setSrc(ready.get(url)); return undefined; }
    let live = true;
    setSrc(null);
    picture(url).then((u) => { if (live) setSrc(u); }).catch(() => { if (live) setSrc(null); });
    return () => { live = false; };
  }, [url]);
  return src;
}

/** Forget a picture that turned out not to load as an image. */
export const forgetPicture = (src) => {
  for (const [url, u] of ready) {
    if (u === src) { ready.delete(url); failed.add(url); }
  }
};

/* ----------------------------------------------------------- mine */

const errorOf = async (r, fallback) => {
  const body = await r.json().catch(() => null);
  return (body && body.error) || fallback;
};

/** Upload my photo: the 512 and the 96, as the crop step made them. */
export async function savePhoto(full, thumb) {
  const form = new FormData();
  form.append('photo', full, full.type === 'image/jpeg' ? 'photo.jpg' : 'photo.webp');
  form.append('thumb', thumb, thumb.type === 'image/jpeg' ? 'thumb.jpg' : 'thumb.webp');
  const r = await fetch(`${API_BASE}/photos/me`, { method: 'PUT', headers: photoAuth(), body: form });
  if (!r.ok) throw new Error(await errorOf(r, r.status === 413 ? 'That photo is too large.' : 'Could not save your photo.'));
  const saved = await r.json();
  // Mine shows at once - here and on every circle of me on this page.
  const me = { ...(state.me || {}), key: saved.key, version: saved.version, shown: true };
  set({ ...state, me, photos: { ...state.photos, [saved.key]: saved.version } });
  loadPhotos({ force: true });
  return saved;
}

/** Remove a photo: mine, or (an administrator) somebody else's by key. */
export async function removePhoto(key = null) {
  const path = key ? `/photos/${String(key).replace(':', '/')}` : '/photos/me';
  const r = await fetch(`${API_BASE}${path}`, { method: 'DELETE', headers: photoAuth() });
  if (!r.ok) throw new Error(await errorOf(r, 'Could not remove that photo.'));
  const gone = key || state.me?.key;
  const photos = { ...state.photos };
  delete photos[gone];
  const me = state.me && state.me.key === gone ? { ...state.me, version: null, shown: false } : state.me;
  set({ ...state, photos, me });
  loadPhotos({ force: true });
  return true;
}

/** For the tests and for signing out: start again from nothing. */
export const resetPhotos = () => { blobs.clear(); ready.clear(); failed.clear(); loadedAt = 0; set(EMPTY); };
